/**
 * Configuración centralizada de opciones de filtros para Invoices/Orders
 * Este archivo define las opciones de filtros que se utilizan tanto en AdvancedFilter
 * como en PrefacturarModal para mantener consistencia en toda la aplicación.
 */
import { FilterStructure } from '@shared/components/ui/AdvancedFilter';

/**
 * Interfaz para el callback de carga de valores dinámicos
 */
export type LoadColumnValuesCallback = (
  columnValue: string,
) => Promise<Array<{ label: string; value: string | number }>>;

/**
 * Interfaz para opciones de país
 */
export interface CountryOption {
  id: string;
  name: string;
  iso2: string;
}

/**
 * Obtiene las opciones de filtros para el módulo de Invoices/Orders
 * @param t - Función de traducción del módulo
 * @param loadColumnValues - Callback para cargar valores dinámicos
 * @param showClientFilter - Si debe mostrarse el filtro de cliente
 * @param countries - Lista de países para el filtro de país
 * @param excludeFinancialStatus - Excluir filtro financial_status (no permitido en endpoint /invoices/shipments/block)
 * @returns Array de configuración de filtros
 */
export const getInvoicesOrdersFilterOptions = (
  t: (key: string) => string,
  loadColumnValues: LoadColumnValuesCallback,
  showClientFilter: boolean = false,
  countries: CountryOption[] = [],
  excludeFinancialStatus: boolean = false,
): FilterStructure[] => {
  const baseFilters: FilterStructure[] = [
    // 🎯 payment_method_id
    {
      column: {
        label: t('filters.paymentMethod'),
        value: 'payment_method_id',
        type: 'select',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: [],
      loadValues: loadColumnValues,
    },
    // 🎯 shop_id
    {
      column: { label: t('filters.store'), value: 'shop_id', type: 'select' },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: [],
      loadValues: loadColumnValues,
    },
    // 🎯 courier_id
    {
      column: {
        label: t('filters.courier'),
        value: 'courier_id',
        type: 'select',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: [],
      loadValues: loadColumnValues,
      persistValues: true,
    },
    // 🎯 created_at
    {
      column: {
        label: t('filters.createdAt'),
        value: 'created_at',
        type: 'date',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
        { label: t('filters.greaterThan'), value: 'greater_than' },
        { label: t('filters.lessThan'), value: 'less_than' },
        { label: t('filters.greaterEqual'), value: 'greater_equal' },
        { label: t('filters.lessEqual'), value: 'less_equal' },
      ],
      values: [],
    },
    // 🎯 shipping_date
    {
      column: {
        label: t('filters.shippingDate'),
        value: 'shipping_date',
        type: 'date',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
        { label: t('filters.greaterThan'), value: 'greater_than' },
        { label: t('filters.lessThan'), value: 'less_than' },
        { label: t('filters.greaterEqual'), value: 'greater_equal' },
        { label: t('filters.lessEqual'), value: 'less_equal' },
      ],
      values: [],
    },
    // 🎯 date_tracking_update (último estado logístico / tracking)
    {
      column: {
        label: t('filters.lastLogisticUpdate'),
        value: 'date_tracking_update',
        type: 'date',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
        { label: t('filters.greaterThan'), value: 'greater_than' },
        { label: t('filters.lessThan'), value: 'less_than' },
        { label: t('filters.greaterEqual'), value: 'greater_equal' },
        { label: t('filters.lessEqual'), value: 'less_equal' },
      ],
      values: [],
    },
    // 🎯 last_status_at
    {
      column: {
        label: t('filters.lastStatusAt'),
        value: 'last_status_at',
        type: 'date',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
        { label: t('filters.greaterThan'), value: 'greater_than' },
        { label: t('filters.lessThan'), value: 'less_than' },
        { label: t('filters.greaterEqual'), value: 'greater_equal' },
        { label: t('filters.lessEqual'), value: 'less_equal' },
      ],
      values: [],
    },
    // 🎯 type (Tipo de shipment: SHIPPING, RETURN, RESHIPMENT)
    {
      column: {
        label: t('filters.type'),
        value: 'type',
        type: 'select',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: [
        { label: t('filters.typeOptions.shipping', 'Envío'), value: 'SHIPPING' },
        { label: t('filters.typeOptions.return', 'Devolución'), value: 'RETURN' },
        { label: t('filters.typeOptions.reshipment', 'Reenvío'), value: 'RESHIPMENT' },
      ],
    },
    // 🎯 country
    {
      column: {
        label: t('filters.country'),
        value: 'country',
        type: 'select',
      },
      actions: [
        { label: t('filters.equals'), value: 'equals' },
        { label: t('filters.notEquals'), value: 'not_equals' },
        { label: t('filters.contains'), value: 'contains' },
        { label: t('filters.notContains'), value: 'not_contains' },
        { label: t('filters.startsWith'), value: 'starts_with' },
        { label: t('filters.endsWith'), value: 'ends_with' },
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: countries.map((country) => ({
        label: country.name,
        value: country.iso2,
      })),
    },
    // 🎯 city
    {
      column: {
        label: t('filters.city'),
        value: 'city',
        type: 'text',
      },
      actions: [
        { label: t('filters.equals'), value: 'equals' },
        { label: t('filters.notEquals'), value: 'not_equals' },
        { label: t('filters.contains'), value: 'contains' },
        { label: t('filters.notContains'), value: 'not_contains' },
        { label: t('filters.startsWith'), value: 'starts_with' },
        { label: t('filters.endsWith'), value: 'ends_with' },
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: null, // 🎯 null para mostrar input de texto libre
    },
    // 🎯 zip
    {
      column: {
        label: t('filters.zip'),
        value: 'zip',
        type: 'number',
      },
      actions: [
        { label: t('filters.equals'), value: 'equals' },
        { label: t('filters.notEquals'), value: 'not_equals' },
        { label: t('filters.greaterThan'), value: 'greater_than' },
        { label: t('filters.lessThan'), value: 'less_than' },
        { label: t('filters.greaterEqual'), value: 'greater_equal' },
        { label: t('filters.lessEqual'), value: 'less_equal' },
        { label: t('filters.contains'), value: 'contains' },
        { label: t('filters.notContains'), value: 'not_contains' },
        { label: t('filters.startsWith'), value: 'starts_with' },
        { label: t('filters.endsWith'), value: 'ends_with' },
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: null, // 🎯 null para mostrar input de número libre
    },
    // 🎯 num_items
    {
      column: {
        label: t('filters.numItems'),
        value: 'num_items',
        type: 'number',
      },
      actions: [
        { label: t('filters.equals'), value: 'equals' },
        { label: t('filters.notEquals'), value: 'not_equals' },
        { label: t('filters.greaterThan'), value: 'greater_than' },
        { label: t('filters.lessThan'), value: 'less_than' },
        { label: t('filters.greaterEqual'), value: 'greater_equal' },
        { label: t('filters.lessEqual'), value: 'less_equal' },
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: null, // 🎯 null para mostrar input de número libre
    },
    // 🎯 period
    {
      column: {
        label: t('filters.period'),
        value: 'period',
        type: 'date',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
        { label: t('filters.greaterThan'), value: 'greater_than' },
        { label: t('filters.lessThan'), value: 'less_than' },
        { label: t('filters.greaterEqual'), value: 'greater_equal' },
        { label: t('filters.lessEqual'), value: 'less_equal' },
      ],
      values: [],
    },
    // 🎯 financial_status (no permitido en endpoint /invoices/shipments/block)
    ...(excludeFinancialStatus
      ? []
      : [
          {
            column: {
              label: t('filters.financialStatus'),
              value: 'financial_status',
              type: 'select',
            },
            actions: [
              { label: t('filters.in'), value: 'in' },
              { label: t('filters.notIn'), value: 'not_in' },
              { label: t('filters.isNull'), value: 'is_null' },
              { label: t('filters.isNotNull'), value: 'is_not_null' },
            ],
            values: [
              { label: t('filters.financialStatusOptions.notInvoiced', 'No facturado'), value: 'NOT_INVOICED' },
              { label: t('filters.financialStatusOptions.preinvoiced', 'Prefacturado'), value: 'PREINVOICED' },
              { label: t('filters.financialStatusOptions.invoiced', 'Facturado'), value: 'INVOICED' },
              { label: t('filters.financialStatusOptions.blocked', 'Bloqueado'), value: 'BLOCKED' },
            ],
          },
        ]),
    // 🎯 status_ids (estado del pedido)
    {
      column: {
        label: t('filters.orderStatus'),
        value: 'status_ids',
        type: 'select',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: [],
      loadValues: loadColumnValues,
    },
    // 🎯 shipment_status_ids (estado de envío)
    {
      column: {
        label: t('filters.shipmentStatus'),
        value: 'shipment_status_ids',
        type: 'select',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: [],
      loadValues: loadColumnValues,
    },
  ];

  // 🎯 Filtro de Cliente - visible si hay datos o fue seleccionado
  if (showClientFilter) {
    baseFilters.push({
      column: {
        label: t('filters.client'),
        value: 'team_id',
        type: 'select',
      },
      actions: [
        { label: t('filters.in'), value: 'in' },
        { label: t('filters.notIn'), value: 'not_in' },
        { label: t('filters.isNull'), value: 'is_null' },
        { label: t('filters.isNotNull'), value: 'is_not_null' },
      ],
      values: [],
      loadValues: loadColumnValues,
      persistValues: true, // 🎯 INDICA QUE SE DEBE PERSISTIR EL LABEL
    });
  }

  return baseFilters;
};
